import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { Download } from "lucide-react";

import { session } from "@/lib/api";
import { portal } from "@/lib/api";
import { sanitizeLegalHtml } from "@/lib/legal";
import type { LegalRegistrationDocument, LegalRequirement, LegalRequirements } from "@/lib/types";
import { Alert, Button, ErrorAlert, ErrorState, FullPageSpinner, Modal } from "@/components/ui";
import { config } from "@/lib/config";

function registrationFileUrl(versionId: string): string {
  return `${config.apiUrl}/v1/legal/registration-requirements/${encodeURIComponent(versionId)}/file?download=true`;
}

function DocumentText({
  title,
  versionId,
  format,
  html,
  publicDocument = false,
}: {
  title: string;
  versionId: string;
  format: "pdf" | "docx";
  html: string | null;
  publicDocument?: boolean;
}) {
  const readable = useQuery({
    queryKey: ["legal-readable", versionId],
    queryFn: () => portal.legalReadable(versionId),
    enabled: !publicDocument && html === undefined,
    staleTime: Infinity,
  });
  const content = publicDocument ? html : readable.data?.html;
  const download = useMutation({
    mutationFn: () => portal.legalFile(versionId),
    onSuccess: (blob) => {
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `${title.replace(/[^a-z0-9]+/gi, "-").replace(/^-|-$/g, "") || "terms"}.${format}`;
      anchor.click();
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    },
  });

  return (
    <div className="space-y-3 rounded-lg border border-border bg-muted/30 p-3">
      {content ? (
        <div
          className="prose prose-sm max-w-none text-fg [&_a]:text-brand-700 [&_a]:underline"
          dangerouslySetInnerHTML={{ __html: sanitizeLegalHtml(content) }}
        />
      ) : (
        <p className="text-sm text-fg-muted">
          This document is a PDF. Open or download it to read the complete version.
        </p>
      )}
      {publicDocument ? (
        <a
          href={registrationFileUrl(versionId)}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-brand-700 underline"
        >
          <Download className="size-4" aria-hidden />
          Download {format.toUpperCase()}
        </a>
      ) : (
        <Button
          type="button"
          variant="secondary"
          size="sm"
          loading={download.isPending}
          onClick={() => download.mutate()}
          icon={<Download className="size-4" aria-hidden />}
        >
          Download {format.toUpperCase()}
        </Button>
      )}
      {download.error && <ErrorAlert error={download.error} />}
    </div>
  );
}

function RegistrationDocument({ document }: { document: LegalRegistrationDocument }) {
  return (
    <details className="rounded-lg border border-border bg-surface p-3" open>
      <summary className="cursor-pointer font-medium text-fg">{document.title}</summary>
      <div className="mt-3 space-y-3">
        {document.description && <p className="text-sm text-fg-muted">{document.description}</p>}
        {document.change_summary && (
          <p className="text-sm text-fg-muted">What changed: {document.change_summary}</p>
        )}
        <p className="text-xs text-fg-subtle">Version {document.version}</p>
        <DocumentText
          title={document.title}
          versionId={document.version_id}
          format={document.format}
          html={document.html}
          publicDocument
        />
      </div>
    </details>
  );
}

export function RegistrationTerms({
  documents,
  checked,
  onChange,
}: {
  documents: LegalRegistrationDocument[];
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  if (documents.length === 0) return null;
  return (
    <fieldset className="space-y-3 rounded-lg border border-border p-3">
      <legend className="px-1 text-sm font-medium text-fg">Terms and data use</legend>
      <div className="space-y-3">
        {documents.map((document) => (
          <RegistrationDocument key={document.document_key} document={document} />
        ))}
      </div>
      <label className="flex items-start gap-2 text-sm text-fg">
        <input
          type="checkbox"
          className="mt-0.5 size-4 rounded border-border accent-brand-600"
          checked={checked}
          onChange={(event) => onChange(event.target.checked)}
          required
        />
        <span>
          I have read and accept the current terms and data-use notices above.
          <span className="sr-only"> Required</span>
        </span>
      </label>
    </fieldset>
  );
}

function RequirementDocument({ requirement }: { requirement: LegalRequirement }) {
  const readable = useQuery({
    queryKey: ["legal-readable", requirement.version_id],
    queryFn: () => portal.legalReadable(requirement.version_id),
    staleTime: Infinity,
  });
  return (
    <details className="rounded-lg border border-border bg-surface p-3" open>
      <summary className="cursor-pointer font-medium text-fg">{requirement.title}</summary>
      <div className="mt-3 space-y-3">
        {requirement.description && <p className="text-sm text-fg-muted">{requirement.description}</p>}
        {requirement.reason === "updated" && (
          <Alert tone="info">
            What changed: {requirement.change_summary ?? "This document has a new version."}
          </Alert>
        )}
        {requirement.reason === "new" && <Alert tone="info">This is a new document for your account.</Alert>}
        <p className="text-xs text-fg-subtle">
          Current version {requirement.version_code ?? `v${requirement.version_number}`}
        </p>
        {readable.isPending ? (
          <FullPageSpinner />
        ) : readable.error ? (
          <ErrorAlert error={readable.error} />
        ) : readable.data ? (
          <DocumentText
            title={requirement.title}
            versionId={requirement.version_id}
            format={requirement.format}
            html={readable.data.html}
          />
        ) : null}
      </div>
    </details>
  );
}

function AcceptanceForm({
  requirements,
  blocking,
  onAccepted,
  onDecline,
}: {
  requirements: LegalRequirement[];
  blocking: boolean;
  onAccepted: () => void;
  onDecline: () => void;
}) {
  const heading = useRef<HTMLHeadingElement>(null);
  const accept = useMutation({
    mutationFn: () => portal.acceptLegal(requirements.map((requirement) => requirement.version_id)),
    onSuccess: onAccepted,
  });
  useEffect(() => heading.current?.focus(), []);
  return (
    <div className="mx-auto max-w-2xl space-y-5 p-6 sm:p-10" aria-labelledby="legal-terms-heading">
      <div className="space-y-2">
        <h1
          id="legal-terms-heading"
          ref={heading}
          tabIndex={-1}
          className="text-2xl font-semibold tracking-tight text-fg outline-none"
        >
          Accept to continue
        </h1>
        <p className="text-sm leading-6 text-fg-muted">
          We have updated the documents that apply to your CortexGrip account. Read each one below before
          continuing.
        </p>
      </div>
      {requirements.map((requirement) => (
        <RequirementDocument key={requirement.document_id} requirement={requirement} />
      ))}
      {accept.error && <ErrorAlert error={accept.error} />}
      <div className="flex flex-wrap items-center gap-3">
        <Button loading={accept.isPending} onClick={() => accept.mutate()}>
          Accept and continue
        </Button>
        <Button type="button" variant="secondary" onClick={onDecline}>
          {blocking ? "Sign out" : "Review later"}
        </Button>
      </div>
      <p className="text-sm text-fg-muted">
        {blocking
          ? "You can sign out and return when you are ready."
          : "You can keep using the rest of the portal, and review these documents again from this notice."}
      </p>
    </div>
  );
}

function PendingNotice({
  requirements,
  children,
}: {
  requirements: LegalRequirement[];
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();
  return (
    <>
      <div className="border-b border-amber-500/30 bg-amber-50 px-4 py-2.5 text-sm text-amber-900">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-2">
          <span>Updated terms are ready to review.</span>
          <Button type="button" variant="secondary" size="sm" onClick={() => setOpen(true)}>
            Review terms
          </Button>
        </div>
      </div>
      {children}
      <Modal open={open} onClose={() => setOpen(false)} title="Updated terms" size="lg">
        <AcceptanceForm
          requirements={requirements}
          blocking={false}
          onAccepted={() => {
            setOpen(false);
            void queryClient.invalidateQueries({ queryKey: ["legal-requirements", "access"] });
          }}
          onDecline={() => setOpen(false)}
        />
      </Modal>
    </>
  );
}

export function LegalTermsBoundary({ children }: { children: ReactNode }) {
  const query = useQuery<LegalRequirements>({
    queryKey: ["legal-requirements", "access"],
    queryFn: portal.legalRequirements,
    staleTime: 60_000,
  });
  if (query.isPending) return <FullPageSpinner />;
  if (query.error) return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;
  const pending = query.data.documents.filter((document) => !document.accepted);
  const blocking = pending.filter((document) => document.blocking);
  if (blocking.length > 0) {
    return (
      <AcceptanceForm
        requirements={blocking}
        blocking
        onAccepted={() => void query.refetch()}
        onDecline={() => void session.signOut()}
      />
    );
  }
  return pending.length > 0 ? (
    <PendingNotice requirements={pending}>{children}</PendingNotice>
  ) : (
    <>{children}</>
  );
}
